import { useEffect, useState } from 'react';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState('bundle');
  // PLANS-CRUD-V1: new-plan creation state
  const [creating, setCreating] = useState(false);
  const [newPlan, setNewPlan] = useState({});
  const [createLoading, setCreateLoading] = useState(false);
  // PLANS-CRUD-V1: delete confirmation state
  const [confirmDelete, setConfirmDelete] = useState(null); // holds plan object when delete is requested
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { role } = useAuth();

  const inp = { padding:'8px 12px', background:'#f9f9f9', border:'1px solid #ddd', borderRadius:6, color:'#000', fontSize:14, outline:'none', width:'100%', boxSizing:'border-box', marginBottom:10 };
  const lbl = { color:'#555', fontSize:11, display:'block', marginBottom:4, fontWeight:600 };

  const load = () => { setLoading(true); client.get('/admin/plans').then(r => setPlans(r.data || [])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const parseFeatures = (raw) => {
    if (!raw) return { signing: [], invoicing: [], bundle: [] };
    let parsed = raw;
    if (typeof raw === 'string') { try { parsed = JSON.parse(raw); } catch { parsed = []; } }
    if (raw && raw.value) { try { parsed = JSON.parse(raw.value); } catch { parsed = []; } }
    if (Array.isArray(parsed)) return { signing: parsed, invoicing: parsed, bundle: parsed };
    return { signing: parsed.signing || [], invoicing: parsed.invoicing || [], bundle: parsed.bundle || [] };
  };

  const startEdit = (p) => {
    const feats = parseFeatures(p.features);
    setEditing(p.name);
    setForm({
      price: p.price ?? 0,
      signing_price: p.signing_price ?? 0,
      invoicing_price: p.invoicing_price ?? 0,
      bundle_price: p.bundle_price ?? 0,
      commission_rate: p.commission_rate ?? 0,
      max_documents: p.max_documents ?? 0,
      max_users: p.max_users ?? 1,
      max_invoices: p.max_invoices ?? -1,
      billing_type: p.billing_type || 'MONTHLY',
      price_per_doc: p.price_per_doc ?? 0,
      description: p.description || '',
      features_signing: feats.signing.join('\n'),
      features_invoicing: feats.invoicing.join('\n'),
      features_bundle: feats.bundle.join('\n'),
      is_featured: !!p.is_featured,
      is_active: p.is_active !== false,
      display_order: p.display_order ?? 0
    });
    setMsg('');
  };

  const save = async () => {
    setSaveLoading(true);
    try {
      const toArr = (s) => (s || '').split('\n').map(x => x.trim()).filter(Boolean);
      const payload = {
        price: parseFloat(form.price) || 0,
        signing_price: parseFloat(form.signing_price) || 0,
        invoicing_price: parseFloat(form.invoicing_price) || 0,
        bundle_price: parseFloat(form.bundle_price) || 0,
        commission_rate: parseFloat(form.commission_rate) || 0,
        max_documents: parseInt(form.max_documents),
        max_users: parseInt(form.max_users),
        max_invoices: parseInt(form.max_invoices),
        billing_type: form.billing_type,
        price_per_doc: parseFloat(form.price_per_doc) || 0,
        description: form.description,
        features: { signing: toArr(form.features_signing), invoicing: toArr(form.features_invoicing), bundle: toArr(form.features_bundle) },
        is_featured: form.is_featured,
        is_active: form.is_active,
        display_order: parseInt(form.display_order) || 0
      };
      await client.patch('/admin/plans/' + editing, payload);
      setMsg('✅ ' + editing + ' updated successfully');
      setEditing(null);
      load();
      setTimeout(() => setMsg(''), 4000);
    } catch (e) {
      setMsg('❌ Failed: ' + (e.response?.data?.message || e.response?.data || e.message));
    } finally {
      setSaveLoading(false);
    }
  };

  // PLANS-CRUD-V1: open create-new-plan modal
  const openCreateModal = () => {
    setCreating(true);
    setNewPlan({
      name: '',
      description: '',
      billing_type: 'MONTHLY',
      price: 0,
      signing_price: 0,
      invoicing_price: 0,
      bundle_price: 0,
      commission_rate: 0,
      max_documents: 0,
      max_users: 1,
      max_invoices: -1,
      price_per_doc: 0,
      display_order: 99,
      is_featured: false,
      is_active: true
    });
    setMsg('');
  };

  // PLANS-CRUD-V1: submit new plan via POST /admin/plans
  const createPlanSubmit = async () => {
    setCreateLoading(true);
    try {
      const name = (newPlan.name || '').trim().toUpperCase();
      if (!name) { setMsg('Name is required'); setCreateLoading(false); return; }
      if (!/^[A-Z][A-Z0-9_]{1,29}$/.test(name)) {
        setMsg('Name must be 2-30 chars, A-Z/0-9/_, starting with a letter');
        setCreateLoading(false); return;
      }
      const payload = {
        name,
        description: newPlan.description || '',
        billing_type: newPlan.billing_type || 'MONTHLY',
        price: parseFloat(newPlan.price) || 0,
        signing_price: parseFloat(newPlan.signing_price) || 0,
        invoicing_price: parseFloat(newPlan.invoicing_price) || 0,
        bundle_price: parseFloat(newPlan.bundle_price) || 0,
        commission_rate: parseFloat(newPlan.commission_rate) || 0,
        max_documents: parseInt(newPlan.max_documents) || 0,
        max_users: parseInt(newPlan.max_users) || 1,
        max_invoices: parseInt(newPlan.max_invoices) || -1,
        price_per_doc: parseFloat(newPlan.price_per_doc) || 0,
        display_order: parseInt(newPlan.display_order) || 99,
        is_featured: !!newPlan.is_featured,
        is_active: newPlan.is_active !== false,
        features: { signing: [], invoicing: [], bundle: [] }
      };
      await client.post('/admin/plans', payload);
      setMsg(' Plan ' + name + ' created successfully');
      setCreating(false);
      load();
      setTimeout(() => setMsg(''), 4000);
    } catch (e) {
      setMsg(' Create failed: ' + (e.response?.data?.message || e.response?.data || e.message));
    } finally {
      setCreateLoading(false);
    }
  };

  // PLANS-CRUD-V1: request delete (shows confirmation modal)
  const requestDelete = (plan) => {
    if (plan.name === 'BASIC') {
      setMsg(' BASIC plan cannot be deleted - it is the default trial plan');
      setTimeout(() => setMsg(''), 4000);
      return;
    }
    setConfirmDelete(plan);
  };

  // PLANS-CRUD-V1: confirm delete via DELETE /admin/plans/{name}
  const confirmDeleteSubmit = async () => {
    if (!confirmDelete) return;
    setDeleteLoading(true);
    try {
      const r = await client.delete('/admin/plans/' + confirmDelete.name);
      if (r.data?.archived) {
        setMsg(' Plan ' + confirmDelete.name + ' archived (was in use by ' + r.data.tenantCount + ' tenant(s))');
      } else if (r.data?.deleted) {
        setMsg(' Plan ' + confirmDelete.name + ' deleted (zero tenants)');
      } else {
        setMsg(' Plan ' + confirmDelete.name + ' processed');
      }
      setConfirmDelete(null);
      setEditing(null);
      load();
      setTimeout(() => setMsg(''), 5000);
    } catch (e) {
      setMsg(' Delete failed: ' + (e.response?.data?.message || e.response?.data || e.message));
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderFeatures = (p) => {
    const feats = parseFeatures(p.features);
    const list = feats[previewMode] || [];
    if (!list.length) return <div style={{ color:'#999', fontSize:12, fontStyle:'italic' }}>No {previewMode} features set</div>;
    return (
      <ul style={{ listStyle:'none', padding:0, margin:'0 0 12px' }}>
        {list.map((f, i) => (
          <li key={i} style={{ color:'#444', fontSize:12, padding:'3px 0', display:'flex', alignItems:'flex-start' }}>
            <span style={{ color:'#1a5cff', marginRight:6, fontWeight:700 }}>✓</span>{f}
          </li>
        ))}
      </ul>
    );
  };

  const priceFor = (p) => {
    if (previewMode === 'signing')   return p.signing_price ?? 0;
    if (previewMode === 'invoicing') return p.invoicing_price ?? 0;
    return p.bundle_price ?? 0;
  };

  const tabBtn = (mode, label, badge) => (
    <button onClick={() => setPreviewMode(mode)} style={{
      padding:'8px 16px', fontSize:13, fontFamily:'inherit', fontWeight:500, cursor:'pointer',
      background: previewMode === mode ? '#fff' : 'transparent',
      color: previewMode === mode ? '#000' : '#555',
      border:'none', borderRadius:7,
      boxShadow: previewMode === mode ? '0 1px 3px rgba(0,0,0,0.06)' : 'none'
    }}>
      {label}{badge && <span style={{ background:'#d1fae5', color:'#065f46', fontSize:10, padding:'1px 6px', borderRadius:3, marginLeft:6 }}>{badge}</span>}
    </button>
  );

  return (
    <div>
      <h1 style={{ color:'#000', fontSize:22, fontWeight:700, margin:'0 0 8px', fontFamily:'Georgia,serif' }}>Plans Management</h1>
      <p style={{ color:'#555', margin:'0 0 18px', fontSize:14 }}>Changes are live immediately on the landing page and dashboard.</p>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div style={{ display:'inline-flex', background:'#f1efe8', border:'1px solid #ddd', borderRadius:10, padding:4 }}>
          {tabBtn('signing', 'Signing only')}
          {tabBtn('invoicing', 'Invoicing only')}
          {tabBtn('bundle', 'Bundle', 'recommended')}
        </div>
        {role === 'SUPER_ADMIN' && (
          <button onClick={openCreateModal} style={{ padding:'10px 20px', background:'#10b981', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}>
            + New Plan
          </button>
        )}
      </div>

      {msg && (
        <div style={{ background: msg.startsWith('❌') ? '#fff5f5' : '#f0fff6', border:'1px solid '+(msg.startsWith('❌')?'#f5c6cb':'#b2dfdb'), borderRadius:8, padding:'10px 16px', color: msg.startsWith('❌')?'#a02a2a':'#0a8a40', fontSize:13, marginBottom:16 }}>
          {msg} <button onClick={()=>setMsg('')} style={{ background:'none', border:'none', color:'inherit', cursor:'pointer', float:'right' }}>✕</button>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))', gap:16 }}>
        {loading ? <div style={{ color:'#555' }}>Loading...</div> : plans.map(p => {
          const isE = editing === p.name;
          const isFeatured = !!p.is_featured;
          const isInactive = p.is_active === false;
          return (
            <div key={p.id} style={{
              background:'#fff', borderRadius:12, padding:24,
              border: isE ? '2px solid #1a5cff' : (isFeatured ? '2px solid #ffc107' : '1px solid #ddd'),
              opacity: isInactive ? 0.6 : 1, position:'relative'
            }}>
              {isFeatured && !isE && <div style={{ position:'absolute', top:-10, right:16, background:'#ffc107', color:'#000', padding:'2px 10px', borderRadius:10, fontSize:10, fontWeight:700 }}>FEATURED</div>}
              {isInactive && !isE && <div style={{ position:'absolute', top:-10, left:16, background:'#999', color:'#fff', padding:'2px 10px', borderRadius:10, fontSize:10, fontWeight:700 }}>INACTIVE</div>}

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
                <div>
                  <div style={{ color:'#000', fontSize:18, fontWeight:700 }}>{p.name}</div>
                  <div style={{ color:'#555', fontSize:12 }}>{p.billing_type} · order: {p.display_order ?? 0}</div>
                </div>
                <div style={{ color:'#1a5cff', fontSize:22, fontWeight:700 }}>
                  ${Number(priceFor(p)).toFixed(0)}<span style={{ color:'#555', fontSize:12 }}>/mo</span>
                </div>
              </div>

              {isE ? (
                <div>
                  <label style={lbl}>Description</label>
                  <input style={inp} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="e.g. Best for growing teams" />

                  <div style={{ background:'#f8f9fb', border:'1px solid #e5e7eb', borderRadius:8, padding:12, marginBottom:12 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'#555', marginBottom:8, textTransform:'uppercase' }}>Pricing per mode</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                      <div><label style={lbl}>Signing $</label><input style={inp} type="number" step="0.01" value={form.signing_price} onChange={e=>setForm({...form,signing_price:e.target.value})} /></div>
                      <div><label style={lbl}>Invoicing $</label><input style={inp} type="number" step="0.01" value={form.invoicing_price} onChange={e=>setForm({...form,invoicing_price:e.target.value})} /></div>
                      <div><label style={lbl}>Bundle $</label><input style={inp} type="number" step="0.01" value={form.bundle_price} onChange={e=>setForm({...form,bundle_price:e.target.value})} /></div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                      <div><label style={lbl}>Commission %</label><input style={inp} type="number" step="0.01" value={form.commission_rate} onChange={e=>setForm({...form,commission_rate:e.target.value})} /></div>
                      <div><label style={lbl}>Billing</label>
                        <select style={inp} value={form.billing_type} onChange={e=>setForm({...form,billing_type:e.target.value})}>
                          <option value="MONTHLY">Monthly</option><option value="YEARLY">Yearly</option><option value="LIFETIME">Lifetime</option><option value="PAY_AS_YOU_GO">Pay as you go</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                    <div><label style={lbl}>Max Docs (-1=unl)</label><input style={inp} type="number" value={form.max_documents} onChange={e=>setForm({...form,max_documents:e.target.value})} /></div>
                    <div><label style={lbl}>Max Invoices (-1=unl)</label><input style={inp} type="number" value={form.max_invoices} onChange={e=>setForm({...form,max_invoices:e.target.value})} /></div>
                    <div><label style={lbl}>Max Users (-1=unl)</label><input style={inp} type="number" value={form.max_users} onChange={e=>setForm({...form,max_users:e.target.value})} /></div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <div><label style={lbl}>Per extra doc $</label><input style={inp} type="number" step="0.01" value={form.price_per_doc} onChange={e=>setForm({...form,price_per_doc:e.target.value})} /></div>
                    <div><label style={lbl}>Display order</label><input style={inp} type="number" value={form.display_order} onChange={e=>setForm({...form,display_order:e.target.value})} /></div>
                  </div>

                  <label style={lbl}>Features — Signing only (one per line)</label>
                  <textarea style={{...inp, minHeight:80, fontFamily:'inherit', resize:'vertical' }} value={form.features_signing} onChange={e=>setForm({...form,features_signing:e.target.value})} />
                  <label style={lbl}>Features — Invoicing only (one per line)</label>
                  <textarea style={{...inp, minHeight:80, fontFamily:'inherit', resize:'vertical' }} value={form.features_invoicing} onChange={e=>setForm({...form,features_invoicing:e.target.value})} />
                  <label style={lbl}>Features — Bundle (one per line)</label>
                  <textarea style={{...inp, minHeight:80, fontFamily:'inherit', resize:'vertical' }} value={form.features_bundle} onChange={e=>setForm({...form,features_bundle:e.target.value})} />

                  <div style={{ display:'flex', gap:16, marginBottom:14, marginTop:4 }}>
                    <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13, color:'#444' }}><input type="checkbox" checked={form.is_featured} onChange={e=>setForm({...form,is_featured:e.target.checked})} /> Featured</label>
                    <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13, color:'#444' }}><input type="checkbox" checked={form.is_active} onChange={e=>setForm({...form,is_active:e.target.checked})} /> Active</label>
                  </div>

                  <div style={{ display:'flex', gap:8 }}>
                    <button style={{ flex:1, padding:'10px', background:saveLoading?'#0e3db3':'#1a5cff', color:'#fff', border:'none', borderRadius:6, cursor:saveLoading?'not-allowed':'pointer', fontWeight:600, opacity:saveLoading?0.7:1 }} onClick={save} disabled={saveLoading}>{saveLoading?'Saving...':'Save Changes'}</button>
                    <button style={{ flex:1, padding:'10px', background:'#f0f0f0', color:'#333', border:'none', borderRadius:6, cursor:'pointer' }} onClick={()=>setEditing(null)}>Cancel</button>
                  </div>
                  {/* PLANS-CRUD-V1: Delete button (hidden for BASIC) */}
                  {p.name !== 'BASIC' && (
                    <button style={{ width:'100%', padding:'8px', background:'#fff5f5', color:'#c0392b', border:'1px solid #fca5a5', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600, marginTop:10 }} onClick={() => requestDelete(p)}>
                      Delete Plan
                    </button>
                  )}
                  {p.name === 'BASIC' && (
                    <div style={{ width:'100%', padding:'8px', background:'#f9f9f9', color:'#999', border:'1px solid #eee', borderRadius:6, fontSize:11, marginTop:10, textAlign:'center', fontStyle:'italic' }}>
                      BASIC plan cannot be deleted (default trial plan)
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {p.description && <div style={{ color:'#666', fontSize:13, marginBottom:12, fontStyle:'italic' }}>{p.description}</div>}
                  <div style={{ fontSize:11, color:'#999', marginBottom:8, textTransform:'uppercase' }}>Showing: {previewMode}</div>
                  <div style={{ borderTop:'1px solid #eee', paddingTop:10, marginBottom:12 }}>{renderFeatures(p)}</div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}><span style={{ color:'#555', fontSize:12 }}>Commission</span><span style={{ color:'#000', fontSize:12, fontWeight:600 }}>{Number(p.commission_rate||0).toFixed(2)}%</span></div>
                  {role==='SUPER_ADMIN' && <button style={{ width:'100%', padding:'10px', background:'#1a5cff', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:13, fontWeight:600, marginTop:8 }} onClick={()=>startEdit(p)}>Edit Plan</button>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* PLANS-CRUD-V1: Create New Plan modal */}
      {creating && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }} onClick={() => setCreating(false)}>
          <div style={{ background:'#fff', borderRadius:12, padding:28, width:520, maxWidth:'100%', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }} onClick={e=>e.stopPropagation()}>
            <h2 style={{ fontFamily:'Georgia,serif', fontSize:20, fontWeight:700, color:'#000', margin:'0 0 6px' }}>Create New Plan</h2>
            <p style={{ color:'#666', fontSize:13, margin:'0 0 20px' }}>Name must be UPPERCASE, 2-30 chars, A-Z/0-9/_, starting with a letter.</p>

            <label style={lbl}>Plan Name *</label>
            <input style={inp} placeholder="e.g. PREMIUM" value={newPlan.name||''} onChange={e=>setNewPlan({...newPlan, name:e.target.value.toUpperCase()})} autoFocus />

            <label style={lbl}>Description</label>
            <input style={inp} placeholder="e.g. Best for growing teams" value={newPlan.description||''} onChange={e=>setNewPlan({...newPlan, description:e.target.value})} />

            <div style={{ background:'#f8f9fb', border:'1px solid #e5e7eb', borderRadius:8, padding:12, marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#555', marginBottom:8, textTransform:'uppercase' }}>Pricing per mode</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                <div><label style={lbl}>Signing $</label><input style={inp} type="number" step="0.01" value={newPlan.signing_price||0} onChange={e=>setNewPlan({...newPlan, signing_price:e.target.value})} /></div>
                <div><label style={lbl}>Invoicing $</label><input style={inp} type="number" step="0.01" value={newPlan.invoicing_price||0} onChange={e=>setNewPlan({...newPlan, invoicing_price:e.target.value})} /></div>
                <div><label style={lbl}>Bundle $</label><input style={inp} type="number" step="0.01" value={newPlan.bundle_price||0} onChange={e=>setNewPlan({...newPlan, bundle_price:e.target.value})} /></div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                <div><label style={lbl}>Commission %</label><input style={inp} type="number" step="0.01" value={newPlan.commission_rate||0} onChange={e=>setNewPlan({...newPlan, commission_rate:e.target.value})} /></div>
                <div><label style={lbl}>Billing</label>
                  <select style={inp} value={newPlan.billing_type||'MONTHLY'} onChange={e=>setNewPlan({...newPlan, billing_type:e.target.value})}>
                    <option value="MONTHLY">Monthly</option><option value="YEARLY">Yearly</option><option value="LIFETIME">Lifetime</option><option value="PAY_AS_YOU_GO">Pay as you go</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
              <div><label style={lbl}>Max Docs (-1=unl)</label><input style={inp} type="number" value={newPlan.max_documents||0} onChange={e=>setNewPlan({...newPlan, max_documents:e.target.value})} /></div>
              <div><label style={lbl}>Max Invoices (-1=unl)</label><input style={inp} type="number" value={newPlan.max_invoices||-1} onChange={e=>setNewPlan({...newPlan, max_invoices:e.target.value})} /></div>
              <div><label style={lbl}>Max Users (-1=unl)</label><input style={inp} type="number" value={newPlan.max_users||1} onChange={e=>setNewPlan({...newPlan, max_users:e.target.value})} /></div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              <div><label style={lbl}>Per extra doc $</label><input style={inp} type="number" step="0.01" value={newPlan.price_per_doc||0} onChange={e=>setNewPlan({...newPlan, price_per_doc:e.target.value})} /></div>
              <div><label style={lbl}>Display order</label><input style={inp} type="number" value={newPlan.display_order||99} onChange={e=>setNewPlan({...newPlan, display_order:e.target.value})} /></div>
            </div>

            <div style={{ display:'flex', gap:16, marginBottom:18, marginTop:4 }}>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13, color:'#444' }}><input type="checkbox" checked={!!newPlan.is_featured} onChange={e=>setNewPlan({...newPlan, is_featured:e.target.checked})} /> Featured</label>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13, color:'#444' }}><input type="checkbox" checked={newPlan.is_active!==false} onChange={e=>setNewPlan({...newPlan, is_active:e.target.checked})} /> Active</label>
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button style={{ flex:1, padding:'11px', background:createLoading?'#0e9e6e':'#10b981', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:createLoading?'not-allowed':'pointer', opacity:createLoading?0.7:1 }} onClick={createPlanSubmit} disabled={createLoading}>
                {createLoading ? 'Creating...' : 'Create Plan'}
              </button>
              <button style={{ flex:1, padding:'11px', background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }} onClick={() => setCreating(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* PLANS-CRUD-V1: Delete confirmation modal */}
      {confirmDelete && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }} onClick={() => setConfirmDelete(null)}>
          <div style={{ background:'#fff', borderRadius:12, padding:28, width:460, maxWidth:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }} onClick={e=>e.stopPropagation()}>
            <h2 style={{ fontFamily:'Georgia,serif', fontSize:20, fontWeight:700, color:'#c0392b', margin:'0 0 12px' }}>Delete Plan: {confirmDelete.name}?</h2>
            <p style={{ color:'#444', fontSize:14, margin:'0 0 16px', lineHeight:1.5 }}>
              This action will check how many tenants currently use this plan.
            </p>
            <div style={{ background:'#fff8e1', border:'1px solid #ffd54f', borderRadius:8, padding:12, marginBottom:18, fontSize:13, color:'#5d4037' }}>
              <strong>Smart delete rules:</strong>
              <ul style={{ margin:'8px 0 0', paddingLeft:18 }}>
                <li>If <strong>0 tenants</strong> use this plan, it will be <strong>permanently deleted</strong>.</li>
                <li>If <strong>1+ tenants</strong> use this plan, it will be <strong>archived</strong> (set inactive) instead of deleted, to preserve existing subscriptions.</li>
              </ul>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button style={{ flex:1, padding:'11px', background:deleteLoading?'#a02828':'#c0392b', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:deleteLoading?'not-allowed':'pointer', opacity:deleteLoading?0.7:1 }} onClick={confirmDeleteSubmit} disabled={deleteLoading}>
                {deleteLoading ? 'Processing...' : 'Yes, delete or archive'}
              </button>
              <button style={{ flex:1, padding:'11px', background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }} onClick={() => setConfirmDelete(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
